import { request, loadOrder } from './api.js';

/**
 * The order side of the UI's world. One object with the calls AND the flags that say which of
 * them exist today, so the screens can be honest without a second code path:
 *
 *   create                           the ENCOMPASS HOST, not us -- see createOrder below
 *   GET  /epc/ui/orders/{id}         exists; reports status, order number and a fixed sentence
 *   POST /epc/ui/orders/{id}/redraw  Phase 2 -- canRedraw is false
 *   POST /epc/ui/orders/{id}/cancel  F5. Live -- canCancel is true; see cancelOrder below
 *
 * Under ?stubHost=1 the app swaps this for stub-orders.js, which simulates the mock's lifecycle
 * and reports every flag true. Flip a flag here only when the capability behind it ships.
 */
export const PLACE_ORDER_ENABLED = true;   // F1: the host creates the transaction; the BFF only reports status

export const productionOrders = {
  stub: false,
  canCreate: PLACE_ORDER_ENABLED,
  canRedraw: false,
  // F5. The BFF endpoint exists: POST /epc/ui/orders/{id}/cancel, authenticated by spending the
  // PAT against ICE's origin call.
  canCancel: true,
  // 5 s. Deliberately not faster: the route this polls is reachable from the internet and the
  // whole flow's rate profile is reasoned about at this interval.
  pollIntervalMs: 5000,

  /**
   * There is no POST /epc/ui/orders and there will not be one. ICE's model is that the officer's
   * host creates the transaction (cert 1.1) and ICE then POSTs transaction.created to our webhook;
   * an order we minted ourselves would be an order Encompass does not know about. So the only
   * dependency this needs is the host adapter, and the only thing it can return is the id ICE
   * assigned. The order NUMBER is ours and does not exist yet -- the poll is what finds it.
   */
  async createOrder({ host }, { order }) {
    const { requestType, documents = [], packageType, rush, instructions, extra } = order;
    const transactionId = await host.createTransaction({
      type: requestType,
      // request.options is ICE's bag, and it is NOT free-form: ICE requires an options schema
      // registered on the product for any requestType whose transactions carry options, and refuses
      // the transaction with a 400 when one is missing. An earlier version of this comment said
      // "none in F1" and left it there -- see docs/epc/options-schema-finding.md. One is now
      // registered for the live request types, declaring packageType, rush, instructions,
      // documentKind and product, none of them required.
      //
      // Anything NOT in that schema is stripped by ICE before we ever see it, so a new option here
      // needs adding to scripts/epc-product-options-schema.json in the same change or it arrives as
      // data quietly missing rather than as an error. `extra` is spread FIRST so a registered
      // property named packageType, rush or instructions cannot silently displace the officer's own
      // answer on the fixed form.
      options: { ...(extra ?? {}), packageType, rush: !!rush, instructions: instructions ?? '' },
      // ICE resources are {id, name, mimeType}; the eFolder's own document type is not part of
      // the shape, and everything the eFolder hands us is a PDF unless it says otherwise.
      resources: documents.map((d) => ({ id: d.id, name: d.name, mimeType: d.mimeType ?? 'application/pdf' })),
    });
    return { transactionId };
  },

  getOrderStatus(deps, { partnerAccessToken, transactionId }) {
    // Passed through as the BFF said it, "received" included: that is the window between our
    // backend accepting the order and ICE having a status for it, and it means still-working.
    return loadOrder(deps, { partnerAccessToken, transactionId });
  },

  redraw(deps, { partnerAccessToken, transactionId }) {
    return request(deps.fetch ?? globalThis.fetch,
      `${deps.baseUrl}/epc/ui/orders/${encodeURIComponent(transactionId)}/redraw`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${partnerAccessToken}` },
      }, deps.timeoutMs);
  },

  cancelOrder(deps, { partnerAccessToken, originId, transactionId }) {
    return request(deps.fetch ?? globalThis.fetch,
      `${deps.baseUrl}/epc/ui/orders/${encodeURIComponent(transactionId)}/cancel`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${partnerAccessToken}`,
        },
        body: JSON.stringify({ originId }),
      }, deps.timeoutMs);
  },
};
